import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { skip } from 'rxjs/operators';
import {
  ContactRelationshipsService,
  ContactsService,
  selectContactRelationshipLatestSuccessfullAction,
  selectContactRelationshipsActionLoading,
  selectLastCreatedContact,
} from 'src/app/core/lists';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import { CreateContactDialogComponent } from '../../../../../../partials/content/general/contact-add-dialog/contact-add.dialog.component';
import { GeneralAutocompleteComponent } from '../../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { StringUtilsService } from '../../../../../../../core/services/string-utils.service';
import * as moment from 'moment';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-entity-relationship-edit-dialog',
  templateUrl: './entity-relationship-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityRelationshipEditComponent implements OnInit, OnDestroy {
  relationshipEditForm: FormGroup;
  viewLoading = true;
  loadingAfterSubmit = false;
  filteredEntities: any[] = [];
  displayButton = false;
  filteredRelationTypeId;
  @ViewChild('relationTypeId') relationTypeId: ElementRef<HTMLInputElement>;
  relationTypeIdList: any = [];
  private subscriptions: any[] = [];
  newContact;
  contactAutocompleteCall = this.contactsService.searchContactRelationByField.bind(
    this.contactsService,
  );
  @ViewChild('contactAutocomplete') contactAutocomplete: GeneralAutocompleteComponent;

  entityType = '';
  category = '';
  relationCategoryList = [];
  relationCategoryDescriptionList = [];
  contactSearchFields = this.stringUtils.getEntitySearchFields('CONTACT');
  minDate = moment(new Date()).format('YYYY-MM-DD');

  constructor(
    private contactsService: ContactsService,
    public dialogRef: MatDialogRef<EntityRelationshipEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private contactRelationshipsService: ContactRelationshipsService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    if (this.data?.entity_id) {
      this.contactAutocompleteCall = this.contactsService.searchContactRelationByField.bind(
        this.contactsService,
        this.data.entity_id,
        false,
      );
    }
    if (this.data?.fromAffiliation) {
      // tslint:disable-next-line:max-line-length
      this.contactAutocompleteCall = this.contactsService.searchContactRelationByField.bind(
        this.contactsService,
        this.data.affiliation_id,
        true,
      );
    }
    const loadingSub = this.store
      .pipe(select(selectContactRelationshipsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);
    this.entityType = this.data.entity_type;
    this.loadCategory();
    const successSubscription = this.store
      .pipe(select(selectContactRelationshipLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          this.dialogRef.close();
        }
      });
    this.subscriptions.push(successSubscription);
    this.initRelationshipForm();
    this.relationshipEditForm.get('relationCategory').valueChanges.subscribe((val) => {
      this.category = val;
      this.contactRelationshipsService
        .getRelationTypeDescription(this.entityType, this.category)
        .subscribe((res) => {
          this.relationCategoryDescriptionList = res.data;
          if (this.data.entity_object) {
            const category = {
              entity_type: this.data.entity_object.entity_type,
              id: this.data.entity_object.relation_category_id,
              name: this.data.entity_object.relation_type,
            };
            const _description = {
              category,
              description_type: this.data.entity_object.relationship_description,
              id: this.data.entity_object.relation_type_id,
            };
            this.relationCategoryDescriptionList.push(_description);
            this.relationshipEditForm.get('relationCategoryDescription').setValue(_description);
          }
        });
    });
    this.viewLoading = false;
    this.cdr.detectChanges();

    const contactSub = this.store
      .pipe(select(selectLastCreatedContact))
      .subscribe((res) => (this.newContact = res));
    this.subscriptions.push(contactSub);
    if (this.data.entity_object) {
      this.relationshipEditForm.get('entityInput').setValue(this.data.entity_object.contact);
      this.contactAutocomplete.setValue(this.data.entity_object.contact);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  initRelationshipForm() {
    this.relationshipEditForm = this.fb.group({
      entity_type: ['CONTACT'],
      entityInput: [null, [Validators.required]],
      title: [
        this.data.entity_object && this.data.entity_object.title
          ? this.data.entity_object.title
          : null,
      ],
      relationCategory: [null, [Validators.required]],
      relationCategoryDescription: [null, [Validators.required]],
      isPrimary: [this.data.entity_object ? this.data.entity_object.is_prime : false],
      financialResponsible: [
        this.data.entity_object ? this.data.entity_object.financial_responsible : false,
      ],
      emergencyContact: [
        this.data.entity_object ? this.data.entity_object.emergency_contact : false,
      ],
      phiAuthorized: [this.data.entity_object ? this.data.entity_object.phi_authorized : false],
      validUntilRevoked: [
        this.data.entity_object ? this.data.entity_object.valid_until_revoked : null,
        Validators.required,
      ],
      authorizedDate: [
        this.data.entity_object ? new Date(this.data.entity_object.authorized_upto_date) : null,
        Validators.required,
      ],
    });
    this.onPhiAuthorizedChange();
    this.onValidUntilRevokedChange();
  }

  onNoClick(): void {
    this.dialogRef.close({ isUpdated: false });
  }

  save() {
    const controls = this.relationshipEditForm.controls;
    this.contactAutocomplete.updateValueAndValidity();
    if (this.relationshipEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    this.loadingAfterSubmit = true;
    this.viewLoading = true;
    const entity_id = controls.entityInput.value.id;
    const entity_type = 'CONTACT';
    const relation_type = controls.relationCategory.value.name;
    const relation_type_id = controls.relationCategoryDescription.value.id;
    const relationship_description = controls.relationCategoryDescription.value.description_type;
    const financial_responsible = controls.financialResponsible.value;
    const title = controls.title.value;
    const emergency_contact = controls.emergencyContact.value;
    const phi_authorized = controls.phiAuthorized.value;
    const valid_until_revoked = controls.validUntilRevoked.value;
    const authorized_upto_date = controls.authorizedDate.value;
    this.viewLoading = false;
    const is_prime = controls.isPrimary.value;
    const response = {
      entity_id,
      entity_type,
      relation_type,
      relation_type_id,
      relationship_description,
      is_prime,
      title,
      phi_authorized,
      emergency_contact,
      financial_responsible,
      valid_until_revoked,
      authorized_upto_date,
    };
    this.closeDialog(response);
  }

  closeDialog(response) {
    this.dialogRef.close({
      done: true,
      response,
    });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.relationshipEditForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  getTitle(): string {
    if (this.data.entity_object) {
      return 'CONTACT_RELATIONSHIP.EDIT_CONTACT_RELATIONSHIP';
    }
    return 'CONTACT_RELATIONSHIP.NEW_CONTACT_RELATIONSHIP';
  }

  openContactDialog() {
    const dialogRef = this.dialog.open(CreateContactDialogComponent, {
      width: '90vw',
      disableClose: true,
      hasBackdrop: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.added) {
        this.relationshipEditForm.get('entityInput').setValue(this.newContact);
        this.contactAutocomplete.setValue(this.newContact);
        this.cdr.detectChanges();
      }
    });
  }

  onContactChange(val) {
    this.relationshipEditForm.controls.entityInput.setValue(val);
    this.relationshipEditForm.controls.entityInput.updateValueAndValidity();
  }

  loadCategory() {
    this.contactRelationshipsService.getRelationTypesCategory(this.entityType).subscribe((res) => {
      if (!res) return;
      this.relationCategoryList = res.data;
      if (this.data.entity_object) {
        const category = {
          entity_type: this.data.entity_object.entity_type,
          id: this.data.entity_object.relation_category_id,
          name: this.data.entity_object.relation_type,
        };
        this.relationCategoryList.push(category);
        this.relationshipEditForm.get('relationCategory').setValue(category);
      }
    });
  }

  getPrimaryString(controlName): string {
    if (this.relationshipEditForm.get(controlName).value) return 'Yes';
    return 'No';
  }

  onPhiAuthorizedChange() {
    if (this.relationshipEditForm.get('phiAuthorized').value === false) {
      this.relationshipEditForm.get('validUntilRevoked').setValue(null);
      this.relationshipEditForm.get('validUntilRevoked').disable();
      this.relationshipEditForm.get('validUntilRevoked').markAsUntouched();
      this.relationshipEditForm.get('validUntilRevoked').markAsPristine();

      this.relationshipEditForm.get('authorizedDate').setValue(null);
      this.relationshipEditForm.get('authorizedDate').disable();
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
    } else {
      this.relationshipEditForm.get('validUntilRevoked').enable();
    }
  }
  onValidUntilRevokedChange() {
    if (this.relationshipEditForm.get('validUntilRevoked').value !== false) {
      this.relationshipEditForm.get('authorizedDate').disable();
      this.relationshipEditForm.get('authorizedDate').setValue(null);
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
    } else {
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
      this.relationshipEditForm.get('authorizedDate').enable();
    }
  }
}
